import { useJsApiLoader } from "@react-google-maps/api";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  createCurbside,
  createInstoreOrder,
  CurbsideOrderPayload,
  InstoreOrderPayload,
  PickupOrderPayload,
  postPickupOrder,
} from "../../apis/services/create-service";
import { upload } from "../../apis/utils/upload";
import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_LIBRARIES } from "../../utils/config";
import { IDocsDriver } from "../../utils/types";
import Button from "../Button";
import Modal from "../Modal";
import Select from "../Select";
import CurbsidePickup from "./CurbsidePickup";
import DropoffInformation from "./DropoffInformation";
import InStorePickup from "./InStorePickup";
import LocationPermissionInstructions from "./LocationPermissionInstructions";
import OrderSummary from "./OrderSummary";
import PackagePickup from "./PackagePickup";
import Payment from "./Payment";
import PickupInformation from "./PickupInformation";
import RecipientDetails from "./RecipientDetails";
import UsersTable from "./UsersTable";

interface CreateServiceModalProps {
  isVisible: boolean;
  onHide: () => void;
  onServiceCreation: () => void;
}

export type IUser = {
  _id: string;
  email: string;
  phone: string;
  fullName: string;
  gender: string;
  state: string;
  isBlocked: boolean;
  isEmailVerified: boolean;
  profileImage: string;
  selectedAccountType?: "individual" | "business";
  username: string;
  createdAt: string;
  updatedAt: string;
};

const serviceTypes = [
  {
    label: "Package pickup",
    value: "package_pickup_meta",
  },
  {
    label: "Curbside pickup",
    value: "curbside_pickup_meta",
  },
  {
    label: "Instore pickup",
    value: "instore_pickup_meta",
  },
];

type ServiceTypeKey =
  | "package_pickup_meta"
  | "curbside_pickup_meta"
  | "instore_pickup_meta";

export type StepType =
  | "info"
  | "order"
  | "pickup"
  | "dropoff"
  | "details"
  | "summary"
  | "payment";

export type RecipientDetailsType = {
  name: string;
  phone: string;
  aptNumber?: string;
  preferredDeliveryMethod: string;
  deliveryInstructions?: string;
  dropoffAptNumber: string;
  isDeliveriesAvailable: string
};

type PackagePickupType = {
  orderInformation: {
    firstName: string;
    lastName: string;
    phone: string;
  };
  pickupInformation: {
    lat: null | number;
    lng: null | number;
    address: string;
    state: string;
    city: string;
    country: string;
  };
  dropoffInformation: {
    lat: null | number;
    lng: null | number;
    address: string;
    state: string;
    city: string;
    country: string;
  };
};

type CurbsidePickupType = {
  orderInformation: {
    orderNumber: string;
    storeName: string;
    proofOfPurchase: File | null;
    storeLink: string;
  };
  pickupInformation: {
    lat: null | number;
    lng: null | number;
    address: string;
    state: string;
    city: string;
    country: string;
  };
  dropoffInformation: {
    lat: null | number;
    lng: null | number;
    address: string;
    state: string;
    city: string;
    country: string;
  };
};

type InstorePickupType = {
  orderInformation: {
    orderNumber: string;
    storeName: string;
    proofOfPurchase: File | null;
    pickupInstructions: string;
  };

  pickupInformation: {
    lat: null | number;
    lng: null | number;
    address: string;
    state: string;
    city: string;
    country: string;
  };
  dropoffInformation: {
    lat: null | number;
    lng: null | number;
    address: string;
    state: string;
    city: string;
    country: string;
  };
};

export type MainFormType = {
  step: StepType;
  user: null | IUser;
  driver: null | IDocsDriver;
  serviceType: ServiceTypeKey | null;
  package_pickup_meta: PackagePickupType;
  recipientDetails: RecipientDetailsType;
  curbside_pickup_meta: CurbsidePickupType;
  instore_pickup_meta: InstorePickupType;
  scheduleTime: null | Date;
  promoCode: string | null;
  maxDistance: number | null;
  paymentIntent: string | null;
  userCoords: {
    lat: number | null;
    lng: number | null;
    address: string;
  };
  serviceCost: null | {
    deliveryCharges: string;
    fee: string;
    userTotal: string;
    discount: number;
    discountedUserTotal: string;
    driverTotal: string;
    distance: number;
  };
};

const CreateServiceModal = ({
  isVisible,
  onHide,
  onServiceCreation,
}: CreateServiceModalProps) => {
  const [isSelectingUser, setIsSelectingUser] = useState(false);
  const [locationError, setLocationError] = useState("");
  const [isLoading, setIsloading] = useState(false)

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY!,
    libraries: GOOGLE_MAPS_LIBRARIES!,
  });

  const mainFormInitialValues: MainFormType = {
    step: "info",
    paymentIntent: null,
    // Service selection
    user: null as IUser | null,
    driver: null,
    serviceType: null,
    promoCode: null,
    maxDistance: null,
    userCoords: {
      lat: null,
      address: "",
      lng: null,
    },
    // Package pickup data
    package_pickup_meta: {
      orderInformation: {
        firstName: "",
        lastName: "",
        phone: "",
      },
      pickupInformation: {
        lat: null as number | null,
        lng: null as number | null,
        address: "",
        state: "",
        city: "",
        country: "",
      },
      dropoffInformation: {
        lat: null as number | null,
        lng: null as number | null,
        address: "",
        city: "",
        country: "",
        state: "",
      },
    },

    // Curbside pickup data
    curbside_pickup_meta: {
      // Add other curbside-specific fields here
      orderInformation: {
        orderNumber: "",
        proofOfPurchase: null as File | null,
        storeLink: "",
        storeName: "",
      },
      pickupInformation: {
        lat: null as number | null,
        lng: null as number | null,
        address: "",
        state: "",
        city: "",
        country: "",
      },
      dropoffInformation: {
        lat: null as number | null,
        lng: null as number | null,
        address: "",
        city: "",
        country: "",
        state: "",
      },
    },

    // In-store pickup data
    instore_pickup_meta: {
      // Add other in-store-specific fields here
      orderInformation: {
        orderNumber: "",
        pickupInstructions: "",
        proofOfPurchase: null,
        storeName: "",
      },
      pickupInformation: {
        lat: null as number | null,
        lng: null as number | null,
        address: "",
        state: "",
        city: "",
        country: "",
      },
      dropoffInformation: {
        lat: null as number | null,
        lng: null as number | null,
        address: "",
        city: "",
        country: "",
        state: "",
      },
    },

    // Recipient details
    recipientDetails: {
      name: "",
      phone: "",
      preferredDeliveryMethod: "",
      aptNumber: "",
      dropoffAptNumber: "",
      deliveryInstructions: "",
      isDeliveriesAvailable: ""
    },
    serviceCost: null,
    scheduleTime: null,
  };

  // Single Formik form to manage all state
  const mainForm = useFormik({
    initialValues: mainFormInitialValues,
    validationSchema: Yup.object().shape({
      user: Yup.object().when("step", {
        is: "info",
        then: (schema) => schema.required("Please select a user"),
      }),
      driver: Yup.object().when("step", {
        is: "payment",
        then: (schema) => schema.required("Please select a driver"),
      }),
      serviceType: Yup.string().when("step", {
        is: "info",
        then: (schema) => schema.required("Please select a service type"),
      }),
      package_pickup_meta: Yup.object().when(["serviceType", "step"], {
        is: (serviceType: string, step: string) =>
          serviceType === "package_pickup_meta" &&
          (step === "order" || step === "pickup" || step === "dropoff"),
        then: (schema) =>
          schema.shape({
            orderInformation: Yup.object().shape({
              firstName: Yup.string().required("This field is required"),
              lastName: Yup.string().required("This field is required"),
              phone: Yup.string().required("This field is required"),
            }),
            pickupInformation: Yup.object().shape({
              lat: Yup.number().required("This field is required"),
              lng: Yup.number().required("This field is required"),
              address: Yup.string().required(
                "Please select a pickup information"
              ),
              state: Yup.string().required("This field is required"),
            }),
            dropoffInformation: Yup.object().shape({
              lat: Yup.number().required("This field is required"),
              lng: Yup.number().required("This field is required"),
              address: Yup.string().required(
                "Please select a pickup information"
              ),
            }),
          }),
      }),
      curbside_pickup_meta: Yup.object().when(["serviceType", "step"], {
        is: (serviceType: string, step: string) =>
          serviceType === "curbside_pickup_meta" &&
          (step === "order" || step === "pickup" || step === "dropoff"),
        then: (schema) =>
          schema.shape({
            orderInformation: Yup.object().shape({
              orderNumber: Yup.string().required("This field is required"),
              storeName: Yup.string().required("This field is required"),
              storeLink: Yup.string()
                .required("This field is required")
                .test("custom-url-format", "Invalid url", (value) => {
                  if (!value) return false;
                  return /^(http:\/\/|https:\/\/|www\.|ftp:\/\/|ftps:\/\/|ws:\/\/|wss:\/\/)/.test(
                    value
                  );
                }),

              proofOfPurchase: Yup.mixed().required("This field is required"),
            }),
            pickupInformation: Yup.object().shape({
              lat: Yup.number().required("This field is required"),
              lng: Yup.number().required("This field is required"),
              address: Yup.string().required(
                "Please select a pickup information"
              ),
              state: Yup.string().required("This field is required"),
            }),
            dropoffInformation: Yup.object().shape({
              lat: Yup.number().required("This field is required"),
              lng: Yup.number().required("This field is required"),
              address: Yup.string().required(
                "Please select a pickup information"
              ),
            }),
          }),
      }),
      instore_pickup_meta: Yup.object().when(["serviceType", "step"], {
        is: (serviceType: string, step: string) =>
          serviceType === "instore_pickup_meta" &&
          (step === "order" || step === "pickup" || step === "dropoff"),
        then: (schema) =>
          schema.shape({
            orderInformation: Yup.object().shape({
              orderNumber: Yup.string().required("This field is required"),
              proofOfPurchase: Yup.mixed().required("This field is required"),
              storeName: Yup.string().required("This field is required"),
            }),
            pickupInformation: Yup.object().shape({
              lat: Yup.number().required("This field is required"),
              lng: Yup.number().required("This field is required"),
              address: Yup.string().required(
                "Please select a pickup information"
              ),
              state: Yup.string().required("This field is required"),
            }),
            dropoffInformation: Yup.object().shape({
              lat: Yup.number().required("This field is required"),
              lng: Yup.number().required("This field is required"),
              address: Yup.string().required(
                "Please select a pickup information"
              ),
            }),
          }),
      }),
      recipientDetails: Yup.object().when("step", {
        is: "details",
        then: (schema) =>
          schema.shape({
            name: Yup.string().required("This field is required"),
            phone: Yup.string().required("This field is required"),
            preferredDeliveryMethod: Yup.string().required(
              "This field is required"
            ),
          }),
      }),
    }),
    // context: { step }, // Pass current step to validation context
    onSubmit: async (values, { resetForm, setFieldValue }) => {
      const isDeliveriesAvailable = Number(values?.recipientDetails?.isDeliveriesAvailable)
      const params = {
        availedOfferId: "",
        discount: isDeliveriesAvailable ? null : values.serviceCost
          ? values.serviceCost?.discount.toString()
          : "",
        discountedUserTotal:
          isDeliveriesAvailable ? null : values.serviceCost?.discountedUserTotal.toString() || "",
        distance: values.serviceCost?.distance!,
        driverTotal: values.serviceCost?.driverTotal.toString() || "",
        promoCode: values.promoCode || "",
        serviceCharges: isDeliveriesAvailable ? null : values.serviceCost?.fee || "",
        userTotal: isDeliveriesAvailable ? null : values.serviceCost?.userTotal || "",
        paymentIntent: values.paymentIntent!,
        userId: values.user?._id!,
        assignedDriverId: values.driver?._id!,
        maxDistance: values.maxDistance! * 1609,
        minDistance: 0,
      };
      if (values.serviceType === "package_pickup_meta") {
        let body: PickupOrderPayload = {
          serviceMeta: {
            deliveryInstruction:
              values.recipientDetails.deliveryInstructions || "",
            deliveryLocation: {
              address: values.package_pickup_meta.dropoffInformation.address,
              city: values.package_pickup_meta.dropoffInformation.city,
              contactName: values.recipientDetails.name,
              country: values.package_pickup_meta.dropoffInformation.country,
              lat: values.package_pickup_meta.dropoffInformation.lat!,
              lng: values.package_pickup_meta.dropoffInformation.lng!,
              locationName:
                values.package_pickup_meta.dropoffInformation.address,
              phone: values.recipientDetails.phone,
              state: values.package_pickup_meta.dropoffInformation.state,
            },
            pickupLocation: {
              address: values.package_pickup_meta.pickupInformation.address,
              city: values.package_pickup_meta.pickupInformation.city,
              country: values.package_pickup_meta.pickupInformation.country,
              lat: values.package_pickup_meta.pickupInformation.lat!,
              lng: values.package_pickup_meta.pickupInformation.lng!,
              locationName:
                values.package_pickup_meta.pickupInformation.address,
              state: values.package_pickup_meta.pickupInformation.state,
              addressCtd: "",
              firstName: values.package_pickup_meta.orderInformation.firstName,
              lastName: values.package_pickup_meta.orderInformation.lastName,
              phoneNumber: values.package_pickup_meta.orderInformation.phone,
            },
            preferredDeliveryMethod: {
              type: values.recipientDetails.preferredDeliveryMethod,
            },
            scheduleTime: values.scheduleTime
              ? values.scheduleTime.toISOString()
              : null,
            serviceMetaType: values.serviceType,
          },
          userCoords: {
            latitude: values.userCoords.lat!,
            longitude: values.userCoords.lng!,
            locationName: values.userCoords.address,
          },
        };
        if (values.recipientDetails.aptNumber) {
          body.serviceMeta.pickupLocation.addressCtd =
            values.recipientDetails.aptNumber;
        }
        if (values.recipientDetails.dropoffAptNumber) {
          body.serviceMeta.deliveryLocation.addressCtd =
            values.recipientDetails.dropoffAptNumber;
        }
        const res = await postPickupOrder(body, params);
        if (res?.success) {
          toast.success("Order has been placed successfully");
          resetForm();
          onHide();
          onServiceCreation();
        } else {
          toast.error(
            res?.error?.error ||
            "Something went wrong while processing your order. Please try again later."
          );
          setFieldValue("step", "summary");
        }
        console.log(res, "package pickup order response");
      } else if (values.serviceType === "curbside_pickup_meta") {
        const fd = new FormData();
        fd.append(
          "file",
          values.curbside_pickup_meta.orderInformation.proofOfPurchase!
        );
        fd.append("userType", "user");
        fd.append("type", "proofOfPurchase");
        const proofOfPurchaseUrl = await upload(fd);
        console.log(proofOfPurchaseUrl, "proof of purchase URL");
        const body: CurbsideOrderPayload = {
          serviceMeta: {
            deliveryInstruction: values.recipientDetails.deliveryInstructions!,
            deliveryLocation: {
              address: values.curbside_pickup_meta.dropoffInformation.address!,
              city: values.curbside_pickup_meta.dropoffInformation.city!,
              contactName: values.recipientDetails.name!,
              country: values.curbside_pickup_meta.dropoffInformation.country!,
              lat: values.curbside_pickup_meta.dropoffInformation.lat!,
              lng: values.curbside_pickup_meta.dropoffInformation.lng!,
              locationName:
                values.curbside_pickup_meta.dropoffInformation.address!,
              phone: values.recipientDetails.phone!,
              state: values.curbside_pickup_meta.dropoffInformation.state!,
            },
            pickupLocation: {
              address: values.curbside_pickup_meta.pickupInformation.address!,
              city: values.curbside_pickup_meta.pickupInformation.city!,
              country: values.curbside_pickup_meta.pickupInformation.country!,
              lat: values.curbside_pickup_meta.pickupInformation.lat!,
              linkForConfirmation:
                values.curbside_pickup_meta.orderInformation.storeLink!,
              lng: values.curbside_pickup_meta.pickupInformation.lng!,
              locationName:
                values.curbside_pickup_meta.pickupInformation.address!,
              orderNum:
                values.curbside_pickup_meta.orderInformation.orderNumber!,
              proofOfPurchase: proofOfPurchaseUrl?.success
                ? proofOfPurchaseUrl?.data?.Location
                : "",
              state: values.curbside_pickup_meta.pickupInformation.state!,
              storeName:
                values.curbside_pickup_meta.orderInformation.storeName!,
            },
            preferredDeliveryMethod: {
              type: values.recipientDetails.preferredDeliveryMethod,
            },
            scheduleTime: values.scheduleTime
              ? values.scheduleTime.toISOString()
              : null,
            serviceMetaType: "curbside_pickup_meta",
          },
          userCoords: {
            latitude: values.userCoords.lat!,
            locationName: values.userCoords.address!,
            longitude: values.userCoords.lng!,
          },
        };
        if (values.recipientDetails.aptNumber) {
          body.serviceMeta.pickupLocation.addressCtd =
            values.recipientDetails.aptNumber;
        }
        if (values.recipientDetails.dropoffAptNumber) {
          body.serviceMeta.deliveryLocation.addressCtd =
            values.recipientDetails.dropoffAptNumber;
        }
        const res = await createCurbside(body, params);
        if (res?.success) {
          toast.success("Order has been placed successfully");
          resetForm();
          onHide();
          onServiceCreation();
        } else {
          toast.error(
            res?.error?.error ||
            "Something went wrong while processing your order. Please try again later."
          );
          setFieldValue("step", "summary");
        }
        console.log("curbside response => ", res);
        console.log("curbside pickup body => ", body);
      } else if (values.serviceType === "instore_pickup_meta") {
        const fd = new FormData();
        fd.append(
          "file",
          values.instore_pickup_meta.orderInformation.proofOfPurchase!
        );
        fd.append("userType", "user");
        fd.append("type", "proofOfPurchase");
        const proofOfPurchaseUrl = await upload(fd);
        console.log(proofOfPurchaseUrl, "proof of purchase URL");
        const body: InstoreOrderPayload = {
          serviceMeta: {
            deliveryInstruction: values.recipientDetails.deliveryInstructions!,
            deliveryLocation: {
              address: values.instore_pickup_meta.dropoffInformation.address,
              city: values.instore_pickup_meta.dropoffInformation.city,
              contactName: values.recipientDetails.name,
              country: values.instore_pickup_meta.dropoffInformation.country,
              lat: values.instore_pickup_meta.dropoffInformation.lat!,
              lng: values.instore_pickup_meta.dropoffInformation.lng!,
              locationName:
                values.instore_pickup_meta.dropoffInformation.address,
              phone: values.recipientDetails.phone,
              state: values.instore_pickup_meta.dropoffInformation.state,
            },
            pickupLocation: {
              address: values.instore_pickup_meta.pickupInformation.address,
              city: values.instore_pickup_meta.pickupInformation.city,
              country: values.instore_pickup_meta.pickupInformation.country,
              lat: values.instore_pickup_meta.pickupInformation.lat!,
              lng: values.instore_pickup_meta.pickupInformation.lng!,
              locationName:
                values.instore_pickup_meta.pickupInformation.address,
              orderNum: values.instore_pickup_meta.orderInformation.orderNumber,
              proofOfPurchase: proofOfPurchaseUrl?.success
                ? proofOfPurchaseUrl?.data?.Location
                : "",
              state: values.instore_pickup_meta.pickupInformation.state,
              storeName: values.instore_pickup_meta.orderInformation.storeName,
            },
            preferredDeliveryMethod: {
              type: values.recipientDetails.preferredDeliveryMethod,
            },
            scheduleTime: values.scheduleTime
              ? values.scheduleTime?.toISOString()
              : null,
            serviceMetaType: "instore_pickup_meta",
          },
          userCoords: {
            latitude: values.userCoords.lat!,
            locationName: values.userCoords.address,
            longitude: values.userCoords.lng!,
          },
        };
        if (values.recipientDetails.aptNumber) {
          body.serviceMeta.pickupLocation.addressCtd =
            values.recipientDetails.aptNumber;
        }
        if (values.recipientDetails.dropoffAptNumber) {
          body.serviceMeta.deliveryLocation.addressCtd =
            values.recipientDetails.dropoffAptNumber;
        }
        if (values.instore_pickup_meta.orderInformation.pickupInstructions) {
          body.serviceMeta.pickupInstruction =
            values.instore_pickup_meta.orderInformation.pickupInstructions;
        }
        const res = await createInstoreOrder(body, params);
        if (res?.success) {
          toast.success("Order has been placed successfully");
          resetForm();
          onHide();
          onServiceCreation();
        } else {
          console.log("error message", res?.error?.error);
          toast.error(
            res?.error?.error ||
            "Something went wrong while processing your order. Please try again later."
          );
          setFieldValue("step", "summary");
        }
        console.log(res, "instore order response =>");
        console.log("instore pickup body =>", body);
      }
      // Handle final submission here
    },
  });
  console.log(mainForm.errors, "mainForm.errors");

  const handleSelectUser = (user: IUser | null) => {
    mainForm.setFieldValue("user", user);
  };

  const moveToNextStep = () => {
    if (mainForm.values.step === "info") {
      mainForm.setFieldValue("step", "order");
    } else if (mainForm.values.step === "order") {
      mainForm.setFieldValue("step", "details");
    } else if (mainForm.values.step === "details") {
      mainForm.setFieldValue("step", "payment");
    }
  };

  const getComponent = useCallback(() => {
    if (mainForm.values.step === "info") {
      return (
        <>
          <div className="flex flex-col md:flex-row my-4 gap-4 w-full justify-between">
            <div className="flex w-full md:w-1/2 flex-col">
              <p className="  text-[1rem] lg:text-xs py-2">User *</p>
              <button
                onClick={() => setIsSelectingUser(true)}
                className="flex h-14 items-center border border-primary rounded-lg px-4"
              >
                {mainForm.values.user && (
                  <img
                    className="w-10 h-10 mr-2 rounded-full"
                    src={mainForm.values.user.profileImage}
                    alt="User"
                  />
                )}
                <p className="  text-[1rem] lg:text-xs">
                  {mainForm.values.user
                    ? mainForm.values.user.fullName
                    : "Please select a user"}
                </p>
              </button>
              {mainForm.errors.user && (
                <div className="label">
                  <span className="label-text-alt text-red-600">
                    {mainForm.errors.user}
                  </span>
                </div>
              )}
            </div>
            <Select
              placeholder="Select service type"
              labelClassName="text-base "
              containerClassName="w-full md:w-1/2"
              onBlur={mainForm.handleBlur}
              name="serviceType"
              error={mainForm.errors.serviceType}
              isInvalid={Boolean(
                mainForm.touched.serviceType && mainForm.errors.serviceType
              )}
              value={mainForm.values.serviceType || ""}
              className="h-14 text-[1rem]"
              label="Service type*"
              onChange={(e) => {
                mainForm.setFieldValue("serviceType", e.target.value);
                mainForm.setFieldValue(
                  "package_pickup_meta",
                  mainFormInitialValues.package_pickup_meta
                );
                mainForm.setFieldValue(
                  "curbside_pickup_meta",
                  mainFormInitialValues.curbside_pickup_meta
                );
                mainForm.setFieldValue(
                  "instore_pickup_meta",
                  mainFormInitialValues.instore_pickup_meta
                );
                mainForm.setFieldValue(
                  "recipientDetails",
                  mainFormInitialValues.recipientDetails
                );
                mainForm.setFieldValue("driver", mainFormInitialValues.driver);
                mainForm.setFieldValue(
                  "scheduleTime",
                  mainFormInitialValues.scheduleTime
                );
                mainForm.setFieldValue(
                  "promoCode",
                  mainFormInitialValues.promoCode
                );
              }}
              options={serviceTypes}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  mainForm.validateForm().then((errors) => {
                    if (!errors.user && !errors.serviceType) {
                      moveToNextStep();
                    } else {
                      mainForm.setTouched({ user: true, serviceType: true });
                    }
                  });
                }
              }}
            />
          </div>
          <div className="col-start-1 col-end-3 flex justify-center mt-6">
            <Button
              type="button"
              isLoading={isLoading}
              className="w-full md:w-32 min-h-0 h-10 md:h-10"
              onClick={() => {
                // Validate current step before proceeding
                mainForm.validateForm().then((errors) => {
                  if (!errors.user && !errors.serviceType) {
                    setIsloading(true)
                    setTimeout(() => {
                      setIsloading(false)
                      moveToNextStep()
                    }, 300)
                  } else {
                    mainForm.setTouched({ user: true, serviceType: true });
                  }
                });
              }}
            >
              Continue
            </Button>
          </div>
        </>
      );
    } else if (mainForm.values.step === "order") {
      switch (mainForm.values.serviceType) {
        case "package_pickup_meta": {
          return (
            <PackagePickup
              form={mainForm}
              setStep={(step) => mainForm.setFieldValue("step", step)}
            />
          );
        }
        case "curbside_pickup_meta": {
          return (
            <CurbsidePickup
              form={mainForm}
              setStep={(step) => mainForm.setFieldValue("step", step)}
            />
          );
        }
        case "instore_pickup_meta": {
          return (
            <InStorePickup
              form={mainForm}
              setStep={(step) => mainForm.setFieldValue("step", step)}
            />
          );
        }
      }
    } else if (mainForm.values.step === "pickup") {
      return <PickupInformation form={mainForm} />;
    } else if (mainForm.values.step === "dropoff") {
      return <DropoffInformation form={mainForm} />;
    } else if (mainForm.values.step === "details") {
      return (
        <RecipientDetails
          onStepChange={(step: StepType) => {
            mainForm.setFieldValue("step", step);
          }}
          form={mainForm}
        />
      );
    } else if (mainForm.values.step === "summary") {
      return (
        <OrderSummary
          onSubmit={() => mainForm.setFieldValue("step", "payment")}
          form={mainForm}
        />
      );
    } else if (mainForm.values.step === "payment") {
      return <Payment form={mainForm} />;
    }
  }, [mainForm]);

  const handleStepChange = () => {
    if (mainForm.values.step === "info") return;
    switch (mainForm.values.step) {
      case "order": {
        mainForm.setFieldValue("step", "info");
        break;
      }
      case "pickup": {
        mainForm.setFieldValue("step", "order");
        break;
      }
      case "dropoff": {
        mainForm.setFieldValue("step", "pickup");
        break;
      }
      case "details": {
        mainForm.setFieldValue("step", "dropoff");
        break;
      }
      case "summary": {
        mainForm.setFieldValue("step", "details");
        break;
      }
      case "payment": {
        mainForm.setFieldValue("step", "summary");
        break;
      }
    }
  };

  const modalHeading = useMemo(() => {
    if (mainForm.values.step === "info") return "Create order";
    return serviceTypes.find(
      (service) => service.value === mainForm.values.serviceType
    )?.label;
  }, [mainForm.values.serviceType, mainForm.values.step]);

  const handleGetAdminLocation = async () => {
    console.log("location function being called!");
    if (navigator.geolocation && "google" in window) {
      console.log("location function being called inside if condition");
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          mainForm.setFieldValue("userCoords.lat", position.coords.latitude);
          mainForm.setFieldValue("userCoords.lng", position.coords.longitude);
          const geocoder = new window.google.maps.Geocoder();
          try {
            const { results } = await geocoder.geocode({
              location: new google.maps.LatLng(
                position.coords.latitude,
                position.coords.longitude
              ),
            });
            if (results) {
              mainForm.setFieldValue(
                "userCoords.address",
                results[0].formatted_address
              );
            }
          } catch (error) {
            setLocationError(
              "Something went wrong while fetching location, please try again later."
            );
          }
        },
        (err) => {
          setLocationError(err.message);
        }
      );
    } else {
      setLocationError("Geolocation is not supported by this browser.");
    }
  };

  useEffect(() => {
    if (isVisible && isLoaded) {
      handleGetAdminLocation();
    }
  }, [isVisible, isLoaded]);

  useEffect(() => {
    const setPickUpAndDropOffLocationToUserLocation = async () => {
      const geocoder = new window.google.maps.Geocoder();
      try {
        const { results } = await geocoder.geocode({
          location: new google.maps.LatLng(
            mainForm.values.userCoords.lat!,
            mainForm.values.userCoords.lng!
          ),
        });
        mainForm.setFieldValue(
          `${mainForm.values.serviceType}.pickupInformation.lat`,
          mainForm.values.userCoords.lat
        );
        mainForm.setFieldValue(
          `${mainForm.values.serviceType}.dropoffInformation.lat`,
          mainForm.values.userCoords.lat
        );
        mainForm.setFieldValue(
          `${mainForm.values.serviceType}.pickupInformation.lng`,
          mainForm.values.userCoords.lng
        );
        mainForm.setFieldValue(
          `${mainForm.values.serviceType}.dropoffInformation.lng`,
          mainForm.values.userCoords.lng
        );
        if (results) {
          mainForm.setFieldValue(
            `${mainForm.values.serviceType}.pickupInformation.address`,
            results[0].formatted_address
          );
          mainForm.setFieldValue(
            `${mainForm.values.serviceType}.dropoffInformation.address`,
            results[0].formatted_address
          );
          const state = results[0]?.address_components?.find((comp) =>
            comp?.types?.includes("administrative_area_level_1")
          )?.long_name;

          const country = results?.[0]?.address_components?.find((comp) =>
            comp?.types?.includes("country")
          )?.long_name;

          const city = results?.[0]?.address_components?.find((comp) =>
            comp?.types?.includes("locality")
          )?.long_name;

          mainForm.setFieldValue(
            `${mainForm.values.serviceType}.pickupInformation.state`,
            state!
          );
          mainForm.setFieldValue(
            `${mainForm.values.serviceType}.pickupInformation.country`,
            country!
          );
          mainForm.setFieldValue(
            `${mainForm.values.serviceType}.pickupInformation.city`,
            city!
          );
          mainForm.setFieldValue(
            `${mainForm.values.serviceType}.dropoffInformation.state`,
            state!
          );
          mainForm.setFieldValue(
            `${mainForm.values.serviceType}.dropoffInformation.country`,
            country!
          );
          mainForm.setFieldValue(
            `${mainForm.values.serviceType}.dropoffInformation.city`,
            city!
          );
        }
      } catch (error) {
        setLocationError(
          "Something went wrong while fetching location, please try again later."
        );
      }
    };

    if (
      mainForm.values.serviceType !== null &&
      mainForm.values.userCoords.lat !== null &&
      mainForm.values.userCoords.lng !== null
    ) {
      setPickUpAndDropOffLocationToUserLocation();
    }
  }, [mainForm.values.userCoords, mainForm.values.serviceType]);

  console.log(mainForm.values, "values");

  console.log(locationError, "location Error");

  return (
    <>
      <Modal
        isVisible={isVisible}
        onHide={onHide}
        heading={modalHeading}
        showBackButton={mainForm.values.step !== "info"}
        backButtonFunction={handleStepChange}
      >
        <div className="md:p-5 px-5">
          {locationError ? <LocationPermissionInstructions /> : getComponent()}
        </div>
        <UsersTable
          isVisible={isSelectingUser}
          onHide={() => setIsSelectingUser(false)}
          selectedUser={mainForm.values.user}
          onSelectUser={handleSelectUser}
        />
      </Modal>
    </>
  );
};

export default CreateServiceModal;
